import localizationService, { AddonServiceEnum, AllChargeableServices, BundleService, DocumentServiceEnum } from '../../services/localization/localization.service';
import { Discount } from '../discount/discount';
import { DiscountedPrice } from './discounted-price';
import { PriceDiscountData, PricingDto } from './dto/pricing.dto';
import { NormalPrice } from './normal-price';
import { PartnerDiscountedPrice } from './partner-discounted-price';
import { PriceDetails, PriceLookupKey } from './types';

export class Pricing {
    private _services: PricingDto['services'];
    private _currency: PricingDto['currency'];
    private _generalDiscount: Discount | undefined;
    private _allActiveDiscounts: Discount[] = [];

    constructor(pricingDto: PricingDto) {
        this._services = pricingDto.services;
        this._currency = pricingDto.currency;
        this._generalDiscount = pricingDto.generalDiscount ? new Discount(pricingDto.generalDiscount) : undefined;

        Object.values(this._services).forEach(servicePrices => {
            servicePrices?.forEach(service => {
                if (service?.discount) {
                    this._allActiveDiscounts.push(new Discount(service.discount));
                }
            });
        });
    }

    static PRODUCT_PRICE_KEYS = {
        [DocumentServiceEnum.LastWillAndTestament]: {
            willSingle: 'wills_single',
            willCouple: 'wills_couple',
        },
        [DocumentServiceEnum.enduringPowerOfAttorney]: {
            enduringPowerOfAttorneyDocument: 'enduring_power_of_attorney_document',
            enduringPowerOfAttorneyDocumentWithRegistration: 'enduring_power_of_attorney_document_with_registration',
        },
        [DocumentServiceEnum.premiumLastWillAndTestament]: {
            premiumWillSingle: 'premium_wills_single',
            premiumWillCouple: 'premium_wills_couple',
        },
        [BundleService.EstatePlanningBundle]: {
            estatePlanningBundleSingle: 'estate_planning_bundle_single',
            estatePlanningBundleCouple: 'estate_planning_bundle_couple',
            estatePlanningBundleSingleWitnessing: 'estate_planning_bundle_single_witnessing',
            estatePlanningBundleCoupleWitnessing: 'estate_planning_bundle_couple_witnessing',
        },
        [AddonServiceEnum.EstatePlanningSession]: {
            estatePlanningSessionNewClient: 'estate_planning_session_new_client',
            estatePlanningSessionReturningClient: 'estate_planning_session_returning_client',
        },
    };

    get currencySymbol(): string {
        return localizationService.currencySymbol(this._currency);
    }

    get generalDiscount(): Discount | undefined {
        return this._generalDiscount ?? this._allActiveDiscounts[0];
    }

    hasPrice(service: AllChargeableServices): boolean {
        return !!this._services[service];
    }

    getDiscountForService(service: AllChargeableServices, lookupKey?: PriceLookupKey): Discount | undefined {
        const servicePrice = this.getServicePrice(service, lookupKey);
        return servicePrice?.discount ? new Discount(servicePrice.discount) : undefined;
    }

    getPriceDetailsForService(service: AllChargeableServices, lookupKey?: PriceLookupKey): PriceDetails | undefined {
        const price = this.getServicePrice(service, lookupKey);
        if (!price) return undefined;
        if (price.partner && price.discountedPrice !== undefined) {
            return new PartnerDiscountedPrice(price.discountedPrice, price.originalPrice, this._currency, price.partner, price.lookupKey);
        } else if (price.discount && price.discountedPrice !== undefined) {
            return new DiscountedPrice(price.discountedPrice, price.originalPrice, this._currency, new Discount(price.discount), price.lookupKey);
        } else {
            return new NormalPrice(price.originalPrice, this._currency, price.lookupKey);
        }
    }

    private getServicePrice(service: AllChargeableServices, lookupKey?: PriceLookupKey): PriceDiscountData | undefined {
        const servicePrices = this._services[service];
        if (!servicePrices) return undefined;
        const price = lookupKey ? servicePrices.find(candidate => candidate.lookupKey === lookupKey) : servicePrices[0];
        if (!price || !Number.isFinite(price.originalPrice) || price.originalPrice <= 0) return undefined;
        if (price.discountedPrice !== undefined && (!Number.isFinite(price.discountedPrice) || price.discountedPrice <= 0)) return undefined;
        return price;
    }
}
